/**
 * As partidas da edição, em duas abas: as que vêm e as que já foram.
 *
 * A estrutura é a do template — `.tournaments-tabs` com dois `.tab-link` e as
 * listas de `.collection-item-matches`. Cada linha traz os dois escudos com o
 * "VS" no meio (ou o placar, nas passadas), o nome do confronto, a data, o
 * local e o botão de assistir.
 *
 * As abas são estado do React, não classe alternada por script como no Webflow.
 * A diferença que importa é de acessibilidade: aqui elas são `role="tab"` de
 * verdade, com `aria-selected` e navegação por seta, e o painel some do DOM
 * quando não está ativo. No template as duas listas ficam sempre montadas e a
 * escondida continua sendo lida por leitor de tela.
 */

import { ehEscudoPadrao } from '@mpc/core';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useEntradaDaSecao } from './animacoes';
import { useEdicaoEmDestaque, proximas, passadas } from './useEdicaoEmDestaque';
import { TeamCrest } from '@/components/ui';
import type { MatchDto, TeamDto } from '@/lib/api';

/** Quantas linhas cabem antes de a home virar a página de calendário. */
const LINHAS = 6;

type Aba = 'proximas' | 'passadas';

export function SecaoTorneios() {
  const raiz = useRef<HTMLElement>(null);
  const [aba, setAba] = useState<Aba>('proximas');
  const { edicao, partidas, times, carregando } = useEdicaoEmDestaque();

  const listas: Record<Aba, MatchDto[]> = {
    proximas: proximas(partidas).slice(0, LINHAS),
    passadas: passadas(partidas).slice(0, LINHAS),
  };

  useEntradaDaSecao(raiz, '[data-entra]', {
    dependencias: [aba, partidas.length],
  });

  if (carregando || !edicao || partidas.length === 0) return null;

  /* A aba que abre é a que tem o que mostrar. Numa edição encerrada, "próximas"
     estaria vazia — e abrir numa lista vazia é pior do que não ter aba. */
  const abaAtiva = listas[aba].length > 0 ? aba : aba === 'proximas' ? 'passadas' : 'proximas';
  const lista = listas[abaAtiva];

  return (
    <section className="tournaments-section" ref={raiz}>
      <div className="container">
        <div className="tournaments-wrapper">
          <h2 className="our-tournaments-title" data-entra>
            Partidas · {edicao.name}
          </h2>

          <div className="tournaments-tabs">
            <div className="tournaments-tabs-menu" role="tablist" data-entra>
              <BotaoDeAba
                ativa={abaAtiva === 'proximas'}
                aoEscolher={() => setAba('proximas')}
                desabilitada={listas.proximas.length === 0}
              >
                Próximas partidas
              </BotaoDeAba>
              <BotaoDeAba
                ativa={abaAtiva === 'passadas'}
                aoEscolher={() => setAba('passadas')}
                desabilitada={listas.passadas.length === 0}
              >
                Partidas disputadas
              </BotaoDeAba>
            </div>

            <div className="tournaments-tabs-content">
              <div className="tournaments-tab-pane" role="tabpanel">
                <div className="upcoming-matches-block">
                  <div className="collection-list-matches">
                    {lista.map((partida) => (
                      <LinhaDePartida
                        key={partida.id}
                        partida={partida}
                        casa={partida.homeTeamId ? times[partida.homeTeamId] : undefined}
                        fora={partida.awayTeamId ? times[partida.awayTeamId] : undefined}
                      />
                    ))}
                  </div>

                  <div className="div-block">
                    <Link to={`/campeonatos/${edicao.slug}`} className="transparent-button">
                      <div className="transparent-button-text">Ver todas as partidas</div>
                      <div className="transparent-button-bg" aria-hidden />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BotaoDeAba({
  ativa,
  desabilitada,
  aoEscolher,
  children,
}: {
  ativa: boolean;
  desabilitada: boolean;
  aoEscolher: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={ativa}
      disabled={desabilitada}
      onClick={aoEscolher}
      className={`tournaments-tab-link${ativa ? ' w--current' : ''}`}
    >
      <div>{children}</div>
    </button>
  );
}

/**
 * Uma linha do confronto.
 *
 * O bloco de cima é o cartaz — escudo, VS ou placar, escudo — e o de baixo é a
 * ficha: nome do confronto, data, local e a ação. É o mesmo par do template, e
 * é o hover dele (escudos crescendo, ícone trocando, faixa ganhando fundo) que
 * mora em `template-interacoes.css`.
 */
function LinhaDePartida({
  partida,
  casa,
  fora,
}: {
  partida: MatchDto;
  casa: TeamDto | undefined;
  fora: TeamDto | undefined;
}) {
  const jogada = partida.homeGoals !== null && partida.awayGoals !== null;

  return (
    <div className="collection-item-matches" data-entra>
      <Link to={`/partidas/${partida.id}`} className="matche-item-link">
        <div className="matches-list-block">
          <div className="match-gradient" aria-hidden />

          <Escudo time={casa} />

          {jogada ? (
            <div className="matches-scores">
              <div className="versus">{partida.homeGoals}</div>
              <div className="score-line match" aria-hidden />
              <div className="versus">{partida.awayGoals}</div>
            </div>
          ) : (
            <div className="versus">VS</div>
          )}

          <Escudo time={fora} />
        </div>

        <div className="matches-details">
          <div className="match-info">
            <div className="matches-teams">
              {casa?.name ?? 'A definir'} vs {fora?.name ?? 'A definir'}
            </div>
            <div className="matches-flex">
              <div className="match-date">
                <IconeDeCalendario />
                <div className="match-date-text">{data(partida.scheduledAt)}</div>
              </div>
              <div className="match-date">
                <IconeDeFase />
                <div className="match-date-text">{fase(partida)}</div>
              </div>
            </div>
          </div>

          <div className="watch-button">
            <div>{jogada ? 'Ver o resultado' : 'Ver o confronto'}</div>
            <div className="watch-icons-block">
              <IconeDePlay className="watch-icon-1" />
              <IconeDePlay className="watch-icon-2" aceso />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

function Escudo({ time }: { time: TeamDto | undefined }) {
  if (time?.crestUrl && !ehEscudoPadrao(time.crestUrl)) {
    return <img src={time.crestUrl} alt="" aria-hidden loading="lazy" className="team-icon" />;
  }
  return (
    <div className="team-icon">
      <TeamCrest team={time} size="xl" />
    </div>
  );
}

const FASES: Record<string, string> = {
  group: 'Fase de grupos',
  quarter: 'Quartas de final',
  semi: 'Semifinal',
  third_place: 'Disputa de 3º lugar',
  final: 'Final',
};

function fase(partida: MatchDto): string {
  const nome = FASES[partida.stage] ?? 'Partida';
  return partida.stage === 'group' && partida.round ? `${nome} · ${partida.round}ª rodada` : nome;
}

function data(epochSegundos: number | null): string {
  if (!epochSegundos) return 'Data a definir';
  return new Date(epochSegundos * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function IconeDeCalendario() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="calender-icon" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconeDeFase() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="calender-icon" aria-hidden>
      <path
        d="M6 4h12v4a6 6 0 0 1-12 0V4ZM9 20h6M12 14v6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** O mesmo desenho em dois estados: apagado e aceso. O hover troca um pelo outro. */
function IconeDePlay({ className, aceso }: { className: string; aceso?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <circle
        cx="12"
        cy="12"
        r="10"
        fill={aceso ? 'var(--_colors---text-color--bright-red)' : 'none'}
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M10 8.5 16 12l-6 3.5v-7Z"
        fill={aceso ? 'var(--_colors---background-color--dark)' : 'currentColor'}
      />
    </svg>
  );
}
